// /app/frontend/src/components/admin/TechnicianLeaderboard.jsx
// Display top-rated technicians with customer review highlights

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Trophy, Star, Award, TrendingUp, Clock, MessageSquare,
  User, Phone, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TechnicianLeaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTech, setExpandedTech] = useState(null);

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/technician-leaderboard`, { headers });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-yellow-500" />;
    if (rank === 2) return <Award className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <Award className="h-6 w-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-gray-500 font-bold">#{rank}</span>;
  };

  const getRatingStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star 
            key={star}
            className={`h-4 w-4 ${star <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
          />
        ))}
        <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Technician Leaderboard
            </CardTitle>
            <CardDescription>Top performers based on customer ratings</CardDescription>
          </div>
          <Badge className="bg-amber-100 text-amber-700">
            {leaderboard.length} Technicians
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {leaderboard.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No technician data yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leaderboard.map((tech) => (
              <div 
                key={tech.installer_id}
                className={`border rounded-lg overflow-hidden transition-all ${
                  tech.rank <= 3 ? 'border-amber-200 bg-amber-50/50' : ''
                }`}
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedTech(expandedTech === tech.installer_id ? null : tech.installer_id)}
                >
                  <div className="flex items-center gap-4">
                    {/* Rank */}
                    <div className="flex-shrink-0">
                      {getRankBadge(tech.rank)}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold truncate">{tech.name}</h3>
                        {tech.specialty && (
                          <Badge variant="outline" className="text-xs">
                            {tech.specialty}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        {tech.reviews_count > 0 && getRatingStars(tech.avg_rating)}
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {tech.reviews_count} reviews
                        </span>
                      </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="flex items-center gap-6 text-sm">
                      <div className="text-center">
                        <div className="font-semibold text-lg">{tech.total_jobs}</div>
                        <div className="text-gray-500 text-xs">Jobs</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-lg text-yellow-600">{tech.five_star_count}</div>
                        <div className="text-gray-500 text-xs">5-Star</div>
                      </div>
                      {tech.avg_duration_minutes && (
                        <div className="text-center">
                          <div className="font-semibold text-lg flex items-center gap-1">
                            <Clock className="h-4 w-4 text-gray-400" />
                            {Math.round(tech.avg_duration_minutes)}m
                          </div>
                          <div className="text-gray-500 text-xs">Avg Time</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Expand arrow */}
                    {tech.recent_reviews.length > 0 && (
                      <div className="flex-shrink-0">
                        {expandedTech === tech.installer_id ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Expanded Reviews */}
                {expandedTech === tech.installer_id && tech.recent_reviews.length > 0 && (
                  <div className="border-t bg-white p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Reviews</h4>
                    <div className="space-y-3">
                      {tech.recent_reviews.map((review, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1">
                              {[1,2,3,4,5].map(star => (
                                <Star 
                                  key={star}
                                  className={`h-3 w-3 ${star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {review.customer_name}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 italic">"{review.comment}"</p>
                          {review.date && (
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(review.date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
